import React, { FC, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import {
	convertToPartnerInitialValues, getFormattedInitialPartnerFields, getFormattedPartnerFields, getPartnerDetailsSchema,
	IPartner,
	partnerInitialValues,
} from '../../constants/new-partner/new-partner-form';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';
import { fetchPartnerDetails, setPartnerDetails } from '../../store/merchnts/actions/partners';
import { IModalWindow } from '../modal/ModalWindow';
import { scrollToError } from '../../utils/formikUtils';
import { useParams } from 'react-router-dom';

export const PartnerDetailsUpdateComponent = (OriginalComponent): (props) => JSX.Element => {
	const NewComponent: FC = (props) => {
		const { t } = useTranslation();
		const { partnerId } = useParams();
		const { merchantState: { partnerDetailsData }, dispatch } = useMerchants();

		const [uploadedFile, setUploadedFile] = useState<string>(null);
		const [isEditMode, setIsEditMode] = useState<boolean>(false);
		const [isLoading, setIsLoading] = useState<boolean>(false);
		const [modalState, setModalState] = useState<IModalWindow>({
			isOpen: false
		});
		const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

		const formik = useFormik<IPartner>({
			initialValues: partnerDetailsData
				? convertToPartnerInitialValues(partnerDetailsData)
				: partnerInitialValues,
			validationSchema: getPartnerDetailsSchema(t),
			onSubmit: () => void 0
		});

		useEffect(() => {
			formik.setValues(convertToPartnerInitialValues(partnerDetailsData));
			if (partnerDetailsData?.logoUrl) {
				setUploadedFile(partnerDetailsData?.logoUrl);
			}
		}, [partnerDetailsData]);

		const handleSave = async (): Promise<void> => {
			formik.handleSubmit();
			if (formik.isValid) {

				const initialPartnerData = getFormattedInitialPartnerFields(partnerDetailsData);
				const changedData = getFormattedPartnerFields(formik.values);
				const initialValues = convertToPartnerInitialValues(partnerDetailsData);

				try {
					changedData.logoUrl = uploadedFile;
					delete changedData.logoFile;
					if (!isEqual(changedData, initialPartnerData)) {
						setIsLoading(true);
						await setPartnerDetails(formik, partnerDetailsData, uploadedFile);
						await fetchPartnerDetails({ dispatch, pid: partnerId });
					}
				} catch (e) {
					console.log(e);
					formik.setValues(initialValues);
				}
				finally {
					setIsLoading(false);
					setIsEditMode(false);
				}

			} else {
				scrollToError(formik.errors);
			}
		};

		const handleCancel = (): void => {
			const initialPartnerData = getFormattedInitialPartnerFields(partnerDetailsData);
			const changedData = getFormattedPartnerFields(formik.values);
			changedData.logoUrl = uploadedFile;
			delete changedData.logoFile;
			const initialValues = convertToPartnerInitialValues(partnerDetailsData);
			if (!isEqual(changedData, initialPartnerData)) {
				setModalState({
          isOpen: true,
          modalTitle: t('MERCHANT.CANCEL_MODAL_TITLE'),
          modalText: t('MERCHANT.CANCEL_MODAL_TEXT'),
          confirmButtonText: t('MERCHANT.CANCEL_MODAL_CONFIRM_BTN_NAME'),
          onConfirm: closeModal,
          onCancel: (): void => {
            closeModal();
            setIsEditMode(false);
            formik.setValues(initialValues);
          }
        });
			} else {
				setIsEditMode(false);
			}
		};

		return (
			<OriginalComponent
				formik={formik}
				uploadedFile={uploadedFile}
				setUploadedFile={setUploadedFile}
				isEditMode={isEditMode}
				setIsEditMode={setIsEditMode}
				isLoading={isLoading}
				modalState={modalState}
				closeModal={closeModal}
				onSave={handleSave}
				onCancel={handleCancel}
				partnerData={partnerDetailsData}
				{...props}
			/>
		);
	};

	return NewComponent;
};